import { Alert, Button, CircularProgress, Container, Grid, Stack, Typography } from "@mui/material";
import Page from "../../minimals/components/page";
import { useSettingsContext } from "../../minimals/components/settings";
import NewPropertyInfo from "./NewPropertyInfo";
import BulletinInfo from "./BulletinInfo";
import { useEffect, useState } from "react";
import produce from "immer";
import { BulletinInformation, County, State, UploadInfo, BulletinLocation, PropertyRecord } from "./types";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { stripDataUriPrefix } from "../utils/stripDataUriPrefix";
import { toBase64 } from "../utils/helpers";
import { PropertyInfo } from "../orderEntry/types/Property";

export default function EditBlacklistProperty() {
  const { themeStretch } = useSettingsContext();
  const [searchParams] = useSearchParams();
  const entityId = Number(searchParams.get("entityId"));

  const baseBulletin: BulletinInformation = {
    state: { id: 0, name: "", abbreviation: "", counties: [] },
    county: { id: 0, stateId: 0, name: "", state: { id: 0, name: "", abbreviation: "", counties: [] } },
    note: ""
  };

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [property, setProperty] = useState<PropertyInfo>({ lots: [""], parcelIds: [""] } as PropertyInfo);
  const [stateValue, setStateValue] = useState<State>({} as State);
  const [county, setCounty] = useState<County>({} as County);
  const [bulletinInfo, setBulletinInfo] = useState<BulletinInformation>(baseBulletin);
  const [uploads, setUploads] = useState<UploadInfo[]>([]);
  const [existingBulletins, setExistingBulletins] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(false);
  const [bulletinStateError, setBulletinStateError] = useState(false);
  const [bulletinError, setBulletinError] = useState(false);
  const [notes, setNotes] = useState("");
  const [locations, setLocations] = useState<Partial<BulletinLocation>[]>([{ stateId: 0 }]);

  useEffect(() => {
    if (!entityId) { setNotFound(true); setLoading(false); return; }
    const fetch = async () => {
      try {
        const { data } = await axios.get<PropertyRecord>(`/api/blacklist/getPropertyRecordById?id=${entityId}`);
        if (!data) { setNotFound(true); return; }
        setProperty({
          address: data.address || "",
          aptNo: data.unit || "",
          city: data.city || "",
          zipCode: data.zip || "",
          section: data.section || "",
          block: data.block || "",
          condoNumber: data.subdivision || "",
          lots: data.lots?.map((l) => l.lotNumber) || [""],
          parcelIds: data.parcels?.map((p) => p.parcelId) || [""]
        } as PropertyInfo);
        if (data.state) setStateValue(data.state);
        if (data.county) setCounty(data.county);
        setNotes(data.notes || "");
        setExistingBulletins(data.bulletins?.map((b: any) => b.bulletin) || []);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [entityId]);

  const addOrEditUpload = (newUpload: UploadInfo, index?: number) => {
    if (index === undefined) {
      setUploads((prev) => [...prev, newUpload]);
      return;
    }
    setUploads((prev) => prev.map((u, i) => (i === index ? newUpload : u)));
  };

  const changeBulletinInfo = (updates: Partial<BulletinInformation>) => {
    setBulletinInfo(produce(bulletinInfo, (draft: any) => {
      for (const [key, value] of Object.entries(updates)) { draft[key] = value; }
    }));
  };

  const handleRemoveUpload = (index: number) => {
    setUploads((prev) => prev.filter((_, i) => i !== index));
  };

  const onBlurProperty = () => {
    if (locations.some((loc) => loc.stateId && loc.stateId > 0)) setBulletinStateError(false);
  };

  const onSubmit = async () => {
    if (uploads.length > 0 && !locations.some((loc) => loc.stateId && loc.stateId > 0)) {
      setBulletinStateError(true);
      return;
    }

    const validLocations = locations
      .filter((loc) => loc.stateId && loc.stateId > 0)
      .map((loc) => ({ stateId: loc.stateId, countyId: loc.countyId }));

    const bulletinsWithBase64 = await Promise.all(
      uploads.map(async (u) => ({
        bulletin: {
          number: u.number,
          date: u.date,
          //@ts-ignore
          underwriterId: u.underwriter.id
        },
        base64File: stripDataUriPrefix(await toBase64(u.file))
      }))
    );

    setSubmitting(true);
    try {
      await axios.post(`/api/blacklist/appendToPropertyRecord?entityId=${entityId}`, {
        property: {
          address: (property?.address || "").trim(),
          city: (property?.city || "").trim(),
          section: (property?.section || "").trim(),
          block: (property?.block || "").trim(),
          unit: (property?.aptNo || "").trim(),
          zip: (property?.zipCode || "").trim(),
          lots: (property?.lots || []).map((l) => ({ lotNumber: (l || "").trim() })).filter((l) => Boolean(l.lotNumber)),
          parcels: (property?.parcelIds || []).map((p) => ({ parcelId: (p || "").trim() })).filter((p) => Boolean(p.parcelId)),
          subdivision: (property?.condoNumber || "").trim(),
          countyId: county?.id,
          stateId: stateValue?.id,
          latitude: 0,
          longitude: 0,
          geocodedAddress: "",
          notes
        },
        locations: validLocations,
        bulletinsWithBase64
      });
      setSuccessMessage(true);
      setSubmitting(false);
    } catch {
      setSubmitting(false);
    }
  };

  if (loading) return <CircularProgress sx={{ m: 4 }} />;
  if (notFound) return <Typography sx={{ m: 4 }}>Record not found.</Typography>;

  return (
    <Page title="Blacklist" width="100%">
      <Container maxWidth={themeStretch ? false : "xl"}>
        <Grid container spacing={5}>
          <Grid item xs={12} md={8}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h4" marginBottom={2}>
                Edit Blacklist Property
              </Typography>
            </Stack>
            <NewPropertyInfo
              propertyInfo={property}
              setPropertyInfo={setProperty}
              stateValue={stateValue}
              setStateValue={setStateValue}
              county={county}
              setCounty={setCounty}
              stateError={false}
              cityError={false}
              addressError={false}
              sblError={false}
              isEditMode
            />
            <BulletinInfo
              bulletinInfo={bulletinInfo}
              setBulletinInfo={changeBulletinInfo}
              uploads={uploads}
              addUpload={addOrEditUpload}
              handleRemoveUpload={handleRemoveUpload}
              bulletinStateError={bulletinStateError}
              notes={notes}
              setNotes={setNotes}
              bulletinError={bulletinError}
              setBulletinError={setBulletinError}
              onBlurProperty={onBlurProperty}
              locations={locations}
              setLocations={setLocations}
              isEditMode
              existingBulletins={existingBulletins}
            />
          </Grid>
        </Grid>
        <Stack direction="row" justifyContent="right" spacing={1.5} width={784} marginTop={8}>
          <Button variant="outlined" color="inherit" onClick={() => window.close()}>
            Cancel
          </Button>
          <Button variant="contained" onClick={onSubmit} disabled={submitting}>
            {submitting ? "Saving..." : "Save"}
          </Button>
          {successMessage && (
            <Alert sx={{ marginTop: 2 }} severity="success">
              Successfully Saved
            </Alert>
          )}
        </Stack>
      </Container>
    </Page>
  );
}
